import { Banner as BaseBanner } from '@dropbox/dig-components/banner';
import { useAtomValue } from 'jotai';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  useSignerAppParams,
  useSignerContext,
} from 'signer-app/signer-experience/signer-experience';
import { useTouchedFieldIds } from 'signer-app/signer-experience/use-validation';
import { accessibleViewAtom } from 'signer-app/signer-signature-document/accessible-document-toggle';
import { useAttachmentsInfo } from 'signer-app/utils/use-attachments-info';
import { useIsMobile } from 'signer-app/utils/use-match-media';

interface Props {
  children: React.ReactNode;
  onClose: () => void;
  type?: 'alert' | 'success';
}

const Banner = ({ children, onClose, type = 'alert' }: Props) => (
  <BaseBanner withCloseButton="Close" type={type} onRequestClose={onClose}>
    <BaseBanner.Message>{children}</BaseBanner.Message>
  </BaseBanner>
);

// separate component for ease of removing later
export function ValidationComponent() {
  const { step, fieldId } = useSignerAppParams();
  const {
    validationErrors,
    requiredFieldsLeft: requiredFields,
    hideFormViewToggle,
    isMobileReady,
    allValid,
  } = useSignerContext();
  const touchedFieldIds = useTouchedFieldIds();
  const isMobile = useIsMobile();
  const isFormView = useAtomValue(accessibleViewAtom);
  const { requiredAttachmentsLeft, hasAttachments } = useAttachmentsInfo();
  const [isBannerOpen, setIsBannerOpen] = useState(true);

  const isFormViewAvailable = !hideFormViewToggle && isMobileReady;

  useEffect(() => {
    if (allValid) {
      setIsBannerOpen(true);
    }
  }, [allValid]);

  // Show the validation error for the active field if it's been touched
  const value =
    fieldId && touchedFieldIds.has(fieldId) && validationErrors[fieldId];

  function closeBanner() {
    setIsBannerOpen(false);
  }

  if (!isBannerOpen) {
    return null;
  }

  if (
    isFormView &&
    requiredAttachmentsLeft === 0 &&
    requiredFields === 0 &&
    allValid &&
    (step === 'sign' || step === 'attachments')
  ) {
    return (
      <Banner onClose={closeBanner} type="success">
        <FormattedMessage
          id="4aa334608f1b51866260e2d76e26b9cf018ffd2233f3cfc24c9a514a3cf2edb5"
          description="notification message in singer flow, shows when all fields are filled by signer"
          defaultMessage="{ isDesktop, select, 1 {You have completed all required fields. Please click 'Continue'.} other {Required fields are done. Tap 'Continue'.} }"
          values={{ isDesktop: isMobile ? 0 : 1 }}
        />
      </Banner>
    );
  }

  if (requiredFields === 0 && allValid && step === 'sign' && !isFormView) {
    return (
      <Banner onClose={closeBanner} type="success">
        {/* eslint-disable-next-line no-nested-ternary */}
        {hasAttachments && requiredAttachmentsLeft > 0 ? (
          isFormViewAvailable ? (
            // when form view is available next will not open attachments drawer, on mobile it will show attachments pseudo field on next
            <FormattedMessage
              id="83a7751b6e480a0f1d3cf453e8bac46ac3415a75a5ee950cd18f3d2af0fb599e"
              description="notification message in singer flow, shows when all fields are filled by signer"
              defaultMessage="{ isDesktop, select, 1 {You have completed all required fields. Please click 'Add attachments'.} other {Required fields are done. Tap 'Next' to upload the requested attachments.} }"
              values={{ isDesktop: isMobile ? 0 : 1 }}
            />
          ) : (
            <FormattedMessage
              id="a19adbd5a7ae5458104f89321dae3113032c3a9be26605611f697c6181ab5aaa"
              description="notification message in singer flow, shows when all fields are filled by signer"
              defaultMessage="{ isDesktop, select, 1 {You have completed all required fields. Please click 'Next'.} other {Required fields are done. Tap on arrow above to go to the next step.
} }"
              values={{
                isDesktop: isMobile ? 0 : 1,
              }}
            />
          )
        ) : (
          <FormattedMessage
            id="4aa334608f1b51866260e2d76e26b9cf018ffd2233f3cfc24c9a514a3cf2edb5"
            description="notification message in singer flow, shows when all fields are filled by signer"
            defaultMessage="{ isDesktop, select, 1 {You have completed all required fields. Please click 'Continue'.} other {Required fields are done. Tap 'Continue'.} }"
            values={{ isDesktop: isMobile ? 0 : 1 }}
          />
        )}
      </Banner>
    );
  }

  if (
    !isFormView &&
    requiredAttachmentsLeft === 0 &&
    requiredFields === 0 &&
    allValid &&
    step === 'attachments'
  ) {
    return (
      <Banner onClose={closeBanner} type="success">
        <FormattedMessage
          id="acafeac12a8a019c0be2ea7e79020719e68be401e96a085de1d8d25a4ff88448"
          description="notification message in singer flow, shows when all attachments are uploaded by signer"
          defaultMessage="{ isDesktop, select, 1 {You have uploaded all required attachments. Please click 'Continue'.} other {Required attachments are done. Tap 'Continue'.} }"
          values={{ isDesktop: isMobile ? 0 : 1 }}
        />
      </Banner>
    );
  }

  if (!value || step !== 'sign') {
    return null;
  }

  return (
    <Banner onClose={closeBanner} type="alert">
      {value.message}
    </Banner>
  );
}
